import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail, fileSrc } from "../../lib/apiClient";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Plus, Trash2, ImageIcon, Link2, ExternalLink, Package, BookmarkPlus, Sparkles, Wand2, Palette, Check } from "lucide-react";
import { toast } from "sonner";
import { ProductPickerDialog } from "../ProductPickerDialog";

export default function MoodBoardTab({ projectId, readOnly, kind = "moodboard" }) {
  const isScheme = kind === "scheme";
  const noun = isScheme ? "Design Scheme" : "Mood Board";
  const { user } = useAuth();
  const isDesigner = user?.role === "designer";
  const [boards, setBoards] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [editLink, setEditLink] = useState(null); // {boardId, image}
  const [editLinkVal, setEditLinkVal] = useState("");
  const [uploadBoard, setUploadBoard] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadLink, setUploadLink] = useState("");
  const [uploadBusy, setUploadBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [smartUrl, setSmartUrl] = useState("");
  const [smartFetching, setSmartFetching] = useState(false);
  const [smartResult, setSmartResult] = useState(null);
  const [createPin, setCreatePin] = useState("");
  const [pinBoard, setPinBoard] = useState(null); // boardId being edited
  const [pinVal, setPinVal] = useState("");
  const [pinBusy, setPinBusy] = useState(false);
  const [pinImporting, setPinImporting] = useState(false);
  const [selected, setSelected] = useState({}); // imageId -> boardId
  const [paletteBoard, setPaletteBoard] = useState(null);
  const [brand, setBrand] = useState("all");
  const [generating, setGenerating] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [chosen, setChosen] = useState({}); // index -> true
  const [savingPalette, setSavingPalette] = useState(false);

  const toggleSelect = (boardId, imgId) =>
    setSelected((s) => {
      const next = { ...s };
      if (next[imgId]) delete next[imgId]; else next[imgId] = boardId;
      return next;
    });
  const selectedForBoard = (boardId) => Object.keys(selected).filter((id) => selected[id] === boardId);

  const openPalette = (boardId) => {
    if (selectedForBoard(boardId).length === 0) { toast.error("Select one or more images first"); return; }
    setPaletteBoard(boardId); setBrand("all"); setCandidates([]); setChosen({});
  };

  const generatePalette = async () => {
    setGenerating(true); setCandidates([]); setChosen({});
    try {
      const ids = selectedForBoard(paletteBoard);
      const { data } = await api.post(`/moodboards/${paletteBoard}/paint-palette`, { image_ids: ids, brand });
      setCandidates(data.colors || []);
      if (!data.colors?.length) toast.error("No colors returned. Try other images.");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not generate palette"); }
    finally { setGenerating(false); }
  };

  const savePalette = async () => {
    const picks = candidates.filter((_, i) => chosen[i]);
    if (picks.length === 0) { toast.error("Tick the colors you want to keep"); return; }
    setSavingPalette(true);
    try {
      await api.post(`/moodboards/${paletteBoard}/palette`, { colors: picks });
      toast.success(`Saved ${picks.length} color${picks.length === 1 ? "" : "s"} to the board`);
      const board = paletteBoard;
      setPaletteBoard(null);
      setSelected((s) => Object.fromEntries(Object.entries(s).filter(([, b]) => b !== board)));
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSavingPalette(false); }
  };

  const removePaletteColor = async (boardId, colorId) => {
    try {
      await api.delete(`/moodboards/${boardId}/palette/${colorId}`);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  // Load the Pinterest widget script and (re)render embeds when boards change
  useEffect(() => {
    if (!boards.some((b) => b.pinterest_url)) return;
    const SRC = "https://assets.pinterest.com/js/pinit.js";
    const build = () => { try { window.PinUtils && window.PinUtils.build(); } catch { /* ignore */ } };
    if (!document.querySelector(`script[src="${SRC}"]`)) {
      const s = document.createElement("script");
      s.src = SRC; s.async = true; s.defer = true;
      s.onload = build;
      document.body.appendChild(s);
    } else {
      setTimeout(build, 300);
    }
  }, [boards]);

  const resetSmart = () => { setSmartUrl(""); setSmartResult(null); };

  const smartImport = async () => {
    const url = smartUrl.trim();
    if (!url) { toast.error("Paste a product or page URL first"); return; }
    setSmartFetching(true);
    try {
      const { data } = await api.post("/products/smart-fill", { url });
      if (!data.image_url) {
        toast.error("Couldn't find an image on that page. Try another link or upload a photo.");
        setSmartResult(null);
      } else {
        setSmartResult({ image_url: data.image_url, name: data.name || "", link: data.link || url });
        toast.success("Image found — review and add to your board");
      }
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSmartFetching(false); }
  };

  const addSmartToBoard = async () => {
    if (!smartResult) return;
    setUploadBusy(true);
    try {
      await api.post(`/moodboards/${uploadBoard}/images/url`, {
        image_url: smartResult.image_url,
        caption: smartResult.name || "",
        link: smartResult.link || "",
      });
      toast.success("Added to board");
      setUploadBoard(null); resetSmart(); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUploadBusy(false); }
  };

  const addFromLibrary = async (p) => {
    if (!p.image_url) { toast.error("That product has no photo to add"); return; }
    try {
      await api.post(`/moodboards/${uploadBoard}/images/url`, {
        image_url: p.image_url,
        caption: p.name || "",
        link: p.link || "",
      });
      toast.success("Added from library");
      setUploadBoard(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const load = () => api.get(`/projects/${projectId}/moodboards${isScheme ? "?kind=scheme" : ""}`).then((r) => setBoards(r.data)).catch(() => {});
  useEffect(() => { load(); }, [projectId]);

  const createBoard = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/projects/${projectId}/moodboards`, { name, description: desc, kind, pinterest_url: isScheme ? "" : createPin.trim() });
      setName(""); setDesc(""); setCreatePin(""); setOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const savePinterest = async (e) => {
    e.preventDefault();
    setPinBusy(true);
    try {
      await api.put(`/moodboards/${pinBoard}/pinterest`, { pinterest_url: pinVal.trim() });
      toast.success(pinVal.trim() ? "Pinterest board linked" : "Pinterest board removed");
      setPinBoard(null); setPinVal(""); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setPinBusy(false); }
  };

  const importPins = async () => {
    if (!pinVal.trim()) { toast.error("Paste a Pinterest board URL first"); return; }
    setPinImporting(true);
    try {
      const { data } = await api.post(`/moodboards/${pinBoard}/pinterest/import`, { pinterest_url: pinVal.trim(), limit: 24 });
      toast.success(`Added ${data.added} pin${data.added === 1 ? "" : "s"} to the board`);
      setPinBoard(null); setPinVal(""); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setPinImporting(false); }
  };

  const openUpload = (boardId) => { setUploadBoard(boardId); setUploadFile(null); setUploadLink(""); resetSmart(); };

  const upload = async (e) => {
    e.preventDefault();
    if (!uploadFile) { toast.error("Please choose an image"); return; }
    setUploadBusy(true);
    const fd = new FormData();
    fd.append("file", uploadFile);
    fd.append("caption", "");
    fd.append("link", uploadLink || "");
    try {
      await api.post(`/moodboards/${uploadBoard}/images`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Image added");
      setUploadBoard(null); setUploadFile(null); setUploadLink("");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUploadBusy(false); }
  };

  const delImage = async (boardId, imageId) => { await api.delete(`/moodboards/${boardId}/images/${imageId}`); load(); };
  const delBoard = async (boardId) => { await api.delete(`/moodboards/${boardId}`); load(); };

  const saveImageToLibrary = async (img) => {
    try {
      const fd = new FormData();
      fd.append("name", img.caption || "Untitled item");
      fd.append("image_url", fileSrc(img.data_url) || "");
      fd.append("link", img.link || "");
      await api.post("/products", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Saved to Product Library");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const openEditLink = (boardId, image) => { setEditLink({ boardId, image }); setEditLinkVal(image.link || ""); };
  const saveLink = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/moodboards/${editLink.boardId}/images/${editLink.image.id}`, { link: editLinkVal });
      toast.success("Link saved");
      setEditLink(null); setEditLinkVal("");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-10" data-testid="moodboard-tab">
      {!readOnly && (
        <div className="flex justify-end">
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-board-btn"
                className="rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs py-5 px-5">
                <Plus size={15} className="mr-2" /> New {noun}
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none border-[#EBE5E0] bg-white">
              <DialogHeader><DialogTitle className="font-serif text-2xl text-[#2B2622]">New {noun.toLowerCase()}</DialogTitle><DialogDescription className="sr-only">Create a new {noun.toLowerCase()}</DialogDescription></DialogHeader>
              <form onSubmit={createBoard} className="space-y-4 mt-2">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#5C5651]">Name</Label>
                  <Input data-testid="board-name-input" required value={name} onChange={(e) => setName(e.target.value)}
                    className="mt-1 rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#5C5651]">Description</Label>
                  <Input data-testid="board-desc-input" value={desc} onChange={(e) => setDesc(e.target.value)}
                    className="mt-1 rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
                </div>
                {!isScheme && (
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#5C5651]">Pinterest board URL (optional)</Label>
                  <Input data-testid="board-pinterest-input" type="url" value={createPin} onChange={(e) => setCreatePin(e.target.value)}
                    placeholder="https://www.pinterest.com/username/board/"
                    className="mt-1 rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
                  <p className="text-[11px] text-[#9b938b] mt-1">Embed a full Pinterest board to share inspiration.</p>
                </div>
                )}
                <Button data-testid="create-board-submit" type="submit"
                  className="w-full rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs py-5">Create board</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {boards.length === 0 ? (
        <div className="border border-dashed border-[#D9CBBF] bg-white p-12 text-center text-[#5C5651]">
          No {noun.toLowerCase()}s yet. {!readOnly && (isScheme ? "Create one to upload final design selections." : "Create one to start curating visuals.")}
        </div>
      ) : (
        boards.map((b) => (
          <div key={b.id} data-testid="board-section">
            <div className="flex items-end justify-between border-b border-[#EBE5E0] pb-3 mb-5">
              <div>
                <h3 className="font-serif text-2xl text-[#2B2622]">{b.name}</h3>
                {b.description && <p className="text-sm text-[#5C5651]">{b.description}</p>}
              </div>
              <div className="flex items-center gap-3">
                {!readOnly && (
                  <>
                    <Button data-testid={`create-palette-${b.id}`} variant="outline" onClick={() => openPalette(b.id)}
                      title="Create a paint palette from selected images"
                      className="rounded-none border-[#506B52] text-[#3f5641] hover:bg-[#EEF2EE] uppercase tracking-wider text-xs py-4 px-4">
                      <Palette size={14} strokeWidth={1.5} className="mr-2" /> Paint Palette
                      {selectedForBoard(b.id).length > 0 && (
                        <span className="ml-2 bg-[#506B52] text-white text-[10px] px-1.5 rounded-full">{selectedForBoard(b.id).length}</span>
                      )}
                    </Button>
                    {!isScheme && (
                    <Button data-testid={`pinterest-${b.id}`} variant="outline" onClick={() => { setPinBoard(b.id); setPinVal(b.pinterest_url || ""); }}
                      className="rounded-none border-[#A68A6B] text-[#8C7154] hover:bg-[#F2EFEB] uppercase tracking-wider text-xs py-4 px-4">
                      <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" className="mr-2" aria-hidden="true"><path d="M12 0C5.37 0 0 5.37 0 12c0 5.08 3.16 9.43 7.63 11.18-.11-.95-.2-2.41.04-3.45.22-.93 1.4-5.94 1.4-5.94s-.36-.72-.36-1.78c0-1.67.97-2.92 2.17-2.92 1.02 0 1.52.77 1.52 1.69 0 1.03-.66 2.57-1 4-.28 1.2.6 2.18 1.79 2.18 2.15 0 3.8-2.27 3.8-5.54 0-2.9-2.08-4.92-5.06-4.92-3.45 0-5.47 2.58-5.47 5.25 0 1.04.4 2.16.9 2.76.1.12.11.23.08.35-.09.37-.29 1.2-.33 1.36-.05.22-.17.27-.4.16-1.5-.7-2.44-2.89-2.44-4.65 0-3.78 2.75-7.26 7.92-7.26 4.16 0 7.39 2.96 7.39 6.92 0 4.13-2.6 7.46-6.22 7.46-1.21 0-2.35-.63-2.74-1.38l-.75 2.84c-.27 1.04-1 2.35-1.49 3.15A12 12 0 0 0 24 12c0-6.63-5.37-12-12-12z"/></svg>
                      {b.pinterest_url ? "Pinterest" : "Add Pinterest"}
                    </Button>
                    )}
                    <Button data-testid={`upload-${b.id}`} onClick={() => openUpload(b.id)}
                      className="rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs py-4 px-4">
                      <Plus size={14} className="mr-2" /> Add Image
                    </Button>
                    <button data-testid={`delete-board-${b.id}`} onClick={() => delBoard(b.id)}
                      className="text-[#5C5651] hover:text-[#9E4747]"><Trash2 size={16} strokeWidth={1.5} /></button>
                  </>
                )}
              </div>
            </div>

            {!isScheme && b.pinterest_url && (
              <div className="mb-6 border border-[#EBE5E0] bg-white p-4 flex flex-col items-center" data-testid={`pinterest-embed-${b.id}`}>
                <a data-pin-do="embedBoard" data-pin-board-width="900" data-pin-scale-height="320" data-pin-scale-width="100"
                  href={b.pinterest_url}>{b.pinterest_url}</a>
                <a href={b.pinterest_url} target="_blank" rel="noopener noreferrer"
                  className="mt-2 inline-flex items-center gap-1 text-[#8C7154] hover:text-[#A68A6B] text-[11px] uppercase tracking-wider">
                  <ExternalLink size={12} strokeWidth={2} /> Open board on Pinterest
                </a>
              </div>
            )}

            {(!b.images || b.images.length === 0) ? (
              !b.pinterest_url && (
                <div className="border border-dashed border-[#D9CBBF] bg-white p-10 text-center text-[#9b938b] flex flex-col items-center gap-2">
                  <ImageIcon size={28} strokeWidth={1} />
                  <span className="text-sm">No images yet</span>
                </div>
              )
            ) : (
              <div className="columns-2 sm:columns-3 lg:columns-4 gap-3 space-y-3">
                {b.images.map((img) => (
                  <div key={img.id} className={`relative group break-inside-avoid ${selected[img.id] ? "ring-2 ring-[#506B52]" : ""}`} data-testid="board-image">
                    {!readOnly && (
                      <button data-testid={`select-image-${img.id}`} onClick={() => toggleSelect(b.id, img.id)}
                        title="Select for paint palette"
                        className={`absolute top-2 left-2 z-10 w-6 h-6 flex items-center justify-center border transition-colors ${selected[img.id] ? "bg-[#506B52] border-[#506B52] text-white" : "bg-white/90 border-white/90 text-transparent hover:text-[#506B52]"}`}>
                        <Check size={14} strokeWidth={3} />
                      </button>
                    )}
                    {img.link ? (
                      <a href={img.link} target="_blank" rel="noopener noreferrer" data-testid={`image-link-${img.id}`}>
                        <img src={fileSrc(img.data_url)} alt={img.caption || "mood"} className="w-full border border-[#EBE5E0]" />
                      </a>
                    ) : (
                      <img src={img.data_url} alt={img.caption || "mood"} className="w-full border border-[#EBE5E0]" />
                    )}
                    {img.link && (
                      <a href={img.link} target="_blank" rel="noopener noreferrer"
                        className="absolute bottom-2 left-2 inline-flex items-center gap-1 bg-white/90 text-[#8C7154] text-[10px] uppercase tracking-wider px-2 py-1">
                        <ExternalLink size={11} strokeWidth={2} /> Visit link
                      </a>
                    )}
                    {!readOnly && (
                      <div className="absolute top-2 right-2 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        {isDesigner && (
                          <button data-testid={`save-image-to-library-${img.id}`} onClick={() => saveImageToLibrary(img)}
                            title="Save to Product Library"
                            className="bg-white/90 p-1.5 text-[#A68A6B] hover:text-[#8C7154]">
                            <BookmarkPlus size={14} strokeWidth={1.5} />
                          </button>
                        )}
                        <button data-testid={`edit-link-${img.id}`} onClick={() => openEditLink(b.id, img)}
                          title={img.link ? "Edit link" : "Add link"}
                          className="bg-white/90 p-1.5 text-[#A68A6B] hover:text-[#8C7154]">
                          <Link2 size={14} strokeWidth={1.5} />
                        </button>
                        <button data-testid={`delete-image-${img.id}`} onClick={() => delImage(b.id, img.id)}
                          className="bg-white/90 p-1.5 text-[#9E4747]">
                          <Trash2 size={14} strokeWidth={1.5} />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {(b.palette && b.palette.length > 0) && (
              <div className="mt-5 border border-[#EBE5E0] bg-white p-4" data-testid={`saved-palette-${b.id}`}>
                <div className="flex items-center gap-2 mb-3">
                  <Palette size={15} strokeWidth={1.5} className="text-[#506B52]" />
                  <span className="text-xs uppercase tracking-wider text-[#3f5641]">Paint palette</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {b.palette.map((c) => (
                    <div key={c.id} className="relative group border border-[#EBE5E0]" data-testid={`palette-color-${c.id}`}>
                      <div className="h-16 w-full" style={{ backgroundColor: c.hex }} />
                      <div className="p-2">
                        <p className="text-[13px] text-[#2B2622] leading-tight">{c.name}</p>
                        <p className="text-[11px] text-[#5C5651]">{c.brand}{c.code ? ` · ${c.code}` : ""}</p>
                        <p className="text-[10px] uppercase tracking-wider text-[#9b938b] mt-0.5">{c.hex}{c.type ? ` · ${c.type}` : ""}</p>
                      </div>
                      {!readOnly && (
                        <button data-testid={`delete-palette-color-${c.id}`} onClick={() => removePaletteColor(b.id, c.id)}
                          className="absolute top-1.5 right-1.5 bg-white/90 p-1 text-[#9E4747] opacity-0 group-hover:opacity-100 transition-opacity">
                          <Trash2 size={13} strokeWidth={1.5} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))
      )}

      <Dialog open={!!uploadBoard} onOpenChange={(o) => { if (!o) { setUploadBoard(null); resetSmart(); } }}>
        <DialogContent className="rounded-none border-[#EBE5E0] bg-white">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-[#2B2622]">Add image</DialogTitle><DialogDescription className="sr-only">Add an image to the mood board</DialogDescription></DialogHeader>

          {isDesigner && (
            <div className="mt-2 border border-[#A68A6B] bg-[#F9F6F1] p-4">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles size={15} className="text-[#A68A6B]" />
                <span className="text-xs uppercase tracking-wider text-[#8C7154]">Smart import</span>
              </div>
              <p className="text-[12px] text-[#5C5651] mb-2">Paste a vendor or inspiration link to pull in the photo &amp; save the source link.</p>
              <div className="flex gap-2">
                <Input data-testid="moodboard-smart-url-input" type="url" value={smartUrl}
                  onChange={(e) => setSmartUrl(e.target.value)} placeholder="https://www.vendor.com/product…"
                  className="flex-1 rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
                <Button data-testid="moodboard-smart-fetch-btn" type="button" onClick={smartImport} disabled={smartFetching}
                  className="rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs px-4">
                  <Wand2 size={14} className="mr-1.5" /> {smartFetching ? "Fetching…" : "Fetch"}
                </Button>
              </div>
              {smartResult && (
                <div data-testid="moodboard-smart-result" className="mt-3 flex gap-3 border border-[#EBE5E0] bg-white p-2">
                  <img src={smartResult.image_url} alt={smartResult.name || "preview"} className="w-20 h-20 object-cover border border-[#EBE5E0]" />
                  <div className="flex-1 min-w-0 flex flex-col justify-between">
                    {smartResult.name && <p className="text-[12px] text-[#2B2622] truncate">{smartResult.name}</p>}
                    <Button data-testid="moodboard-smart-add-btn" type="button" onClick={addSmartToBoard} disabled={uploadBusy}
                      className="rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs py-3 mt-1 w-full">
                      {uploadBusy ? "Adding…" : "Add to Board"}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          <form onSubmit={upload} className="space-y-4 mt-2">
            <Button data-testid="moodboard-pick-product-btn" type="button" variant="outline" onClick={() => setPickerOpen(true)}
              className="w-full rounded-none border-[#A68A6B] text-[#8C7154] hover:bg-[#F2EFEB] uppercase tracking-wider text-xs py-4">
              <Package size={14} className="mr-2" /> Choose from Product Library
            </Button>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5C5651]">Image file</Label>
              <input data-testid="upload-file-input" type="file" accept="image/*"
                onChange={(e) => setUploadFile(e.target.files[0])}
                className="mt-1 block w-full text-sm text-[#5C5651] file:mr-3 file:border file:border-[#A68A6B] file:bg-transparent file:text-[#A68A6B] file:px-4 file:py-2 file:uppercase file:text-xs file:tracking-wider" />
              {uploadFile && <p className="text-[11px] text-[#9b938b] mt-1 truncate">{uploadFile.name}</p>}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5C5651]">Link (optional)</Label>
              <Input data-testid="upload-link-input" type="url" placeholder="https://… product or source page" value={uploadLink}
                onChange={(e) => setUploadLink(e.target.value)}
                className="mt-1 rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
            </div>
            <Button data-testid="upload-submit" type="submit" disabled={uploadBusy}
              className="w-full rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs py-5">
              {uploadBusy ? "Adding…" : "Add Image"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editLink} onOpenChange={(o) => !o && setEditLink(null)}>
        <DialogContent className="rounded-none border-[#EBE5E0] bg-white">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-[#2B2622]">Image link</DialogTitle><DialogDescription className="sr-only">Set or edit the image link</DialogDescription></DialogHeader>
          <form onSubmit={saveLink} className="space-y-4 mt-2">
            <p className="text-sm text-[#5C5651]">Add a link (e.g., product or source page). Clicking the image will open it.</p>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5C5651]">Link URL</Label>
              <Input data-testid="edit-link-input" type="url" placeholder="https://…" value={editLinkVal}
                onChange={(e) => setEditLinkVal(e.target.value)}
                className="mt-1 rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
            </div>
            <Button data-testid="save-link-submit" type="submit"
              className="w-full rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs py-5">
              Save Link
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <ProductPickerDialog open={pickerOpen} onOpenChange={setPickerOpen} onSelect={addFromLibrary} />

      <Dialog open={!!paletteBoard} onOpenChange={(o) => { if (!o) setPaletteBoard(null); }}>
        <DialogContent className="rounded-none border-[#EBE5E0] bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="paint-palette-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#2B2622]">Create Paint Palette</DialogTitle>
            <DialogDescription className="text-sm text-[#5C5651]">
              Finding complementary paint colors from {paletteBoard ? selectedForBoard(paletteBoard).length : 0} selected image(s).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5C5651]">Paint brand</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {[
                  { v: "benjamin_moore", l: "Benjamin Moore" },
                  { v: "sherwin_williams", l: "Sherwin-Williams" },
                  { v: "farrow_ball", l: "Farrow & Ball" },
                  { v: "all", l: "Mix of all brands" },
                ].map((opt) => (
                  <button key={opt.v} data-testid={`brand-${opt.v}`} type="button" onClick={() => setBrand(opt.v)}
                    className={`border px-3 py-2.5 text-sm text-left transition-colors ${brand === opt.v ? "border-[#506B52] bg-[#EEF2EE] text-[#3f5641]" : "border-[#EBE5E0] text-[#5C5651] hover:border-[#A68A6B]"}`}>
                    {opt.l}
                  </button>
                ))}
              </div>
            </div>

            <Button data-testid="generate-palette-btn" onClick={generatePalette} disabled={generating}
              className="w-full rounded-none bg-[#506B52] hover:bg-[#3f5641] text-white uppercase tracking-wider text-xs py-5">
              <Sparkles size={14} className="mr-2" /> {generating ? "Analyzing images…" : (candidates.length ? "Regenerate Palette" : "Generate Palette")}
            </Button>

            {candidates.length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-wider text-[#A68A6B] mb-2">Tick the colors to save</p>
                <div className="grid grid-cols-2 gap-2">
                  {candidates.map((c, i) => (
                    <button key={i} type="button" data-testid={`candidate-color-${i}`} onClick={() => setChosen((s) => ({ ...s, [i]: !s[i] }))}
                      className={`flex items-stretch border text-left transition-colors ${chosen[i] ? "border-[#506B52] ring-1 ring-[#506B52]" : "border-[#EBE5E0]"}`}>
                      <span className="w-12 shrink-0" style={{ backgroundColor: c.hex }} />
                      <span className="p-2 flex-1">
                        <span className="block text-[13px] text-[#2B2622] leading-tight">{c.name}</span>
                        <span className="block text-[11px] text-[#5C5651]">{c.brand}{c.code ? ` · ${c.code}` : ""}</span>
                        <span className="block text-[10px] uppercase tracking-wider text-[#9b938b]">{c.type}</span>
                      </span>
                      {chosen[i] && <span className="self-start m-1 text-[#506B52]"><Check size={16} strokeWidth={3} /></span>}
                    </button>
                  ))}
                </div>
                <Button data-testid="save-palette-btn" onClick={savePalette} disabled={savingPalette}
                  className="w-full mt-4 rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs py-5">
                  {savingPalette ? "Saving…" : "Save Selected to Mood Board"}
                </Button>
                <p className="text-[11px] text-[#9b938b] mt-2">Only the colors you tick are kept — the rest are discarded when you close.</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pinBoard} onOpenChange={(o) => { if (!o) { setPinBoard(null); setPinVal(""); } }}>
        <DialogContent className="rounded-none border-[#EBE5E0] bg-white">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#2B2622]">Pinterest board</DialogTitle>
            <DialogDescription className="sr-only">Link or remove a Pinterest board</DialogDescription>
          </DialogHeader>
          <form onSubmit={savePinterest} className="space-y-4 mt-2">
            <p className="text-sm text-[#5C5651]">Paste a public Pinterest board URL to embed it here. Leave blank to remove.</p>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5C5651]">Pinterest board URL</Label>
              <Input data-testid="pinterest-url-input" type="url" placeholder="https://www.pinterest.com/username/board/"
                value={pinVal} onChange={(e) => setPinVal(e.target.value)}
                className="mt-1 rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
            </div>
            <Button data-testid="pinterest-save-submit" type="submit" disabled={pinBusy || pinImporting}
              className="w-full rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs py-5">
              {pinBusy ? "Saving…" : "Save & Embed Board"}
            </Button>
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-[#EBE5E0]" />
              <span className="text-[10px] uppercase tracking-wider text-[#9b938b]">or</span>
              <div className="flex-1 h-px bg-[#EBE5E0]" />
            </div>
            <Button data-testid="pinterest-import-btn" type="button" variant="outline" onClick={importPins} disabled={pinImporting || pinBusy}
              className="w-full rounded-none border-[#A68A6B] text-[#8C7154] hover:bg-[#F2EFEB] uppercase tracking-wider text-xs py-5">
              <Wand2 size={14} className="mr-2" /> {pinImporting ? "Importing…" : "Import Top Pins as Images"}
            </Button>
            <p className="text-[11px] text-[#9b938b]">Pulls the board's recent pins in as individual images — they display everywhere and can be saved to your Product Library. Board must be public.</p>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
